import React, { useState, useEffect, useContext } from 'react';
import { IoClose } from 'react-icons/io5';
import { AppContext } from '../../context/appContext';
import { getBooks } from '../api/bookApi';
import { getBookCategoriesDropdown } from '../api/bookCategoryApi';

/**
 * BookPickerModal - Browse and select books
 * @param {boolean} isOpen - Modal open state
 * @param {function} onClose - Close modal callback
 * @param {function} onSelect - Selection callback (bookId, bookTitle)
 */
const BookPickerModal = ({ isOpen, onClose, onSelect }) => {
  const { t, isRTL } = useContext(AppContext);

  const [books, setBooks] = useState([]);
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(false);
  const [search, setSearch] = useState('');
  const [categoryFilter, setCategoryFilter] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const itemsPerPage = 10;

  // Fetch categories for filter
  useEffect(() => {
    if (isOpen) {
      fetchCategories();
    }
  }, [isOpen]);

  // Fetch books only when a category is selected
  useEffect(() => {
    if (isOpen && categoryFilter) {
      fetchBooks();
    } else {
      setBooks([]);
    }
  }, [isOpen, search, categoryFilter, currentPage]);

  const fetchCategories = async () => {
    try {
      const response = await getBookCategoriesDropdown();
      if (response?.data?.data && Array.isArray(response.data.data)) {
        setCategories(response.data.data);
      } else if (response?.data && Array.isArray(response.data)) {
        setCategories(response.data);
      } else {
        setCategories([]);
      }
    } catch (error) {
      console.error('Error fetching book categories:', error);
      setCategories([]);
    }
  };

  const fetchBooks = async () => {
    setLoading(true);
    try {
      const response = await getBooks(
        currentPage,
        itemsPerPage,
        search,
        categoryFilter || undefined
      );

      if (response?.data?.data && Array.isArray(response.data.data)) {
        setBooks(response.data.data);
        if (response.data.pagination) {
          setTotalPages(response.data.pagination.totalPages || 1);
        }
      } else if (response?.data && Array.isArray(response.data)) {
        setBooks(response.data);
        if (response.pagination) {
          setTotalPages(response.pagination.totalPages || 1);
        }
      } else {
        setBooks([]);
      }
    } catch (error) {
      console.error('Error fetching books:', error);
      setBooks([]);
      setTotalPages(1);
    } finally {
      setLoading(false);
    }
  };

  const handleSelect = (book) => {
    onSelect({
      bookId: book._id,
      bookTitle: book.title
    });
    onClose();
  };

  const handleSearchChange = (e) => {
    setSearch(e.target.value);
    setCurrentPage(1);
  };

  const handleCategoryChange = (e) => {
    setCategoryFilter(e.target.value);
    setCurrentPage(1);
  };

  if (!isOpen) return null;

  return (
    <div className="modal modal-open">
      <div className="modal-box max-w-2xl" dir={isRTL ? 'rtl' : 'ltr'}>
        {/* Header */}
        <div className="flex justify-between items-center mb-4">
          <h3 className="font-bold text-lg">
            {t('common.selectBook') || 'Select Book'}
          </h3>
          <button onClick={onClose} className="btn btn-sm btn-circle btn-ghost">
            <IoClose size={20} />
          </button>
        </div>

        {/* Search */}
        <div className="mb-4">
          <input
            type="text"
            placeholder={t('common.search') || 'Search...'}
            className="input input-bordered w-full"
            value={search}
            onChange={handleSearchChange}
          />
        </div>

        {/* Category Selection (Required) */}
        {categories.length > 0 && (
          <div className="mb-4">
            <label className="label">
              <span className="label-text font-medium">
                {t('common.selectCategory') || 'Select Category'} <span className="text-red-500">*</span>
              </span>
            </label>
            <select
              className="select select-bordered w-full"
              value={categoryFilter}
              onChange={handleCategoryChange}
            >
              <option value="">{t('common.chooseCategoryFirst') || '-- Choose a Category First --'}</option>
              {categories.map(cat => (
                <option key={cat._id} value={cat._id}>
                  {cat.title}
                </option>
              ))}
            </select>
          </div>
        )}

        {/* Book List */}
        <div className="overflow-y-auto max-h-96">
          {!categoryFilter ? (
            <div className="text-center py-8 text-gray-400">
              <svg className="mx-auto h-12 w-12 mb-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6.253v13m0-13C10.832 5.477 9.246 5 7.5 5S4.168 5.477 3 6.253v13C4.168 18.477 5.754 18 7.5 18s3.332.477 4.5 1.253m0-13C13.168 5.477 14.754 5 16.5 5c1.747 0 3.332.477 4.5 1.253v13C19.832 18.477 18.247 18 16.5 18c-1.746 0-3.332.477-4.5 1.253" />
              </svg>
              <p>{t('common.selectCategoryFirstToViewBooks') || 'Please select a category first to view books'}</p>
            </div>
          ) : loading ? (
            <div className="flex justify-center py-8">
              <span className="loading loading-spinner loading-md"></span>
            </div>
          ) : books.length === 0 ? (
            <div className="text-center py-8 text-gray-500">
              {t('common.noBooksInCategory') || 'No books found in this category'}
            </div>
          ) : (
            <div className="space-y-2">
              {books.map(book => (
                <div
                  key={book._id}
                  onClick={() => handleSelect(book)}
                  className="p-3 border rounded-lg hover:bg-base-200 cursor-pointer transition-colors flex gap-3"
                >
                  {book.cover && (
                    <img
                      src={typeof book.cover === 'string' ? book.cover : book.cover?.thumbnail}
                      alt={book.title}
                      className="w-12 h-16 object-cover rounded"
                    />
                  )}
                  <div className="flex-1">
                    <div className="font-medium">{book.title}</div>
                    {book.pageCount && (
                      <div className="text-xs text-gray-500 mt-1">
                        {book.pageCount} {t('book.pages') || 'pages'}
                      </div>
                    )}
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Pagination */}
        {totalPages > 1 && (
          <div className="flex justify-center gap-2 mt-4">
            <button
              className="btn btn-sm"
              onClick={() => setCurrentPage(p => Math.max(1, p - 1))}
              disabled={currentPage === 1}
            >
              {t('common.previous') || 'Previous'}
            </button>
            <span className="py-2 px-3">
              {currentPage} / {totalPages}
            </span>
            <button
              className="btn btn-sm"
              onClick={() => setCurrentPage(p => Math.min(totalPages, p + 1))}
              disabled={currentPage === totalPages}
            >
              {t('common.next') || 'Next'}
            </button>
          </div>
        )}

        {/* Cancel Button */}
        <div className="modal-action">
          <button onClick={onClose} className="btn">
            {t('common.cancel') || 'Cancel'}
          </button>
        </div>
      </div>
      <div className="modal-backdrop" onClick={onClose}></div>
    </div>
  );
};

export default BookPickerModal;
